import { useCallback, useMemo } from 'react';
import {
  ElementsTransformEventArgs,
  RectCenteredElement,
  LinePathElement,
  HighwayElement,
  AngledHighwayElement,
  PosePartial,
  Element,
} from '@thive/canvas';
import {
  LogicalSpace,
  Obstacle,
  Point,
  Polygon,
  Rectangle,
  Segment,
  Group,
} from '@thive/fddm-types';
import { MathUtils, Vector2, Vector3 } from 'three';
import { degToRad } from 'three/src/math/MathUtils.js';

import { assertNever, isUuid } from '@/helpers/utils';
import { convertV2ToV3, rotateVector } from '@/modules/common/helpers/math';
import { NumUtils } from '@/modules/common/utils';
import { VectorUtils } from '@/modules/common/utils/VectorUtils';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import {
  areasToFlatMap,
  isHighwayArea,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers';
import {
  templatesToFlatCollection,
  createEmptyAreas,
} from '@/modules/floorplanDesign/helpers/helpers';
import {
  AtLeast1,
  isAtLeast3,
  minItemsArrayUtils,
} from '@/modules/floorplanDesign/helpers/minItems';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { Area, Template } from '@/modules/floorplanDesign/types';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { ORIGIN_GROUP_ID, OriginServiceFactory } from '@/modules/origin';
import {
  isLassie,
  isLassieMini,
  isShuttleRack,
  isHikStorage,
  TpsServiceFactory,
} from '@/modules/templating';
import { logger } from '@common/logging/logger';
import { CopyPasteServiceFactory } from '@/modules/copy';

import { FddmServiceFactory } from '../services/Floorplan/FddmServiceFactory';
import { CanvasServiceFactory } from '../services/Canvas/CanvasServiceFactory';
import { HistoryServiceFactory } from '../services/history/HistoryServiceFactory';
import { METERS_TO_MILLIMETERS_FACTOR } from '../factories/element';
import { useCanvasStore } from '../store';
import {
  isAreaCanvasElement,
  isGroupCanvasElement,
  isLogicalSpaceCanvasElement,
  isObstacleCanvasElement,
  isStableAngledHighwayElement,
  isStableHighwayElement,
  isStableLinePathElement,
  isStableRectCenteredElement,
  isTemplateCanvasElement,
} from '../typeguards';

// TODO: Reduce duplicate code

const getGroupItemIds = (group: Group): string[] => [
  ...group.areaIds,
  ...group.nestedGroups.flatMap((nestedGroup) => getGroupItemIds(nestedGroup)),
];

type TransformSelection = {
  selectedTemplates: Template[];
  selectedGroups: Group[];
  selectedContainerIds: Set<string>;
  selectedContainerChildIds: Set<string>;
  topLevelSelectionIds: string[];
};

const getTransformSelection = (
  elements: readonly Element[],
  floorplanDesignState: ReturnType<typeof useFloorplanDesignStore.getState>,
): TransformSelection => {
  const { getTemplatesByIds, getGroupsByIds } = floorplanDesignState;
  const templateElements = elements.filter(isTemplateCanvasElement);
  const groupElements = elements.filter(isGroupCanvasElement);
  const selectedTemplates = templatesToFlatCollection(
    getTemplatesByIds(templateElements.map((element) => element.id)),
  );
  const selectedGroups = getGroupsByIds(groupElements.map((element) => element.id));
  const selectedContainerIds = new Set([
    ...templateElements.map((element) => element.id),
    ...groupElements.map((element) => element.id),
  ]);
  const selectedContainerChildIds = new Set([
    ...selectedTemplates.flatMap((template) => getGroupItemIds(template.group)),
    ...selectedGroups.flatMap((group) => getGroupItemIds(group)),
  ]);
  const topLevelSelectionIds = elements
    .map((element) => element.id)
    .filter((id) => !selectedContainerChildIds.has(id));

  return {
    selectedTemplates,
    selectedGroups,
    selectedContainerIds,
    selectedContainerChildIds,
    topLevelSelectionIds,
  };
};

export const useTransformEnd = () => {
  const tpsSvc = useMemo(() => TpsServiceFactory.getInstance().createTpsService(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const canvasSvc = useMemo(() => CanvasServiceFactory.getInstance().createCanvasService(), []);
  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);
  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);

  const restoreSelection = useCallback(
    (selectionIds: string[]): void => {
      const canvas = useCanvasStore.getState().instance;
      if (!canvas) return;

      const existingSelectionIds = canvas.getElements(selectionIds).map((element) => element.id);

      if (existingSelectionIds.length === 0) {
        canvas.deselectAll();
        appSvc.resetSelection();
        return;
      }

      appSvc.setSelectedIds(existingSelectionIds);
      canvasSvc.setSelection(existingSelectionIds);
    },
    [appSvc, canvasSvc],
  );

  /**
   * @description Rebuilds the segments from local canvas points by applying the element's final pose.
   * The canvas rotates around the bounding box center, which changes both position and rotation
   * while the points stay local, so the rotation must be applied before the translation.
   */
  const updateSegmentsFromPose = (
    segments: AtLeast1<Segment>,
    position: Vector3,
    rotation: Vector3,
    points: readonly Vector3[],
  ): AtLeast1<Segment> => {
    const toWorldInMeters = ({ x, y }: Vector3): Vector3 =>
      VectorUtils.mmToMeters(
        convertV2ToV3(rotateVector(new Vector2(x, y), rotation.z)).add(position),
      );

    return minItemsArrayUtils.mapAtLeast1(segments, (segment, index) => {
      const start = toWorldInMeters(points[index]);
      const end = toWorldInMeters(points[index + 1]);

      return {
        ...segment,
        start: { x: start.x, y: start.y },
        end: { x: end.x, y: end.y },
      };
    });
  };

  const updateSegmentPoints = (
    segment: Segment,
    position: Vector3,
    rotation: Vector3,
    size: Vector2,
  ): AtLeast1<Segment> => {
    const halfLength = size.x / 2;
    const angle = rotation.z;
    const pointA = VectorUtils.mmToMeters(
      convertV2ToV3(rotateVector(new Vector2(-halfLength, 0), angle)),
    );
    const pointB = VectorUtils.mmToMeters(
      convertV2ToV3(rotateVector(new Vector2(halfLength, 0), angle)),
    );
    const scaledPosition = VectorUtils.mmToMeters(position.clone());

    const start = scaledPosition.clone().add(pointA);
    const end = scaledPosition.clone().add(pointB);

    return [
      {
        ...segment,
        leftSideWidth: NumUtils.mmToMeters(size.y / 2),
        rightSideWidth: NumUtils.mmToMeters(size.y / 2),
        start: { x: start.x, y: start.y },
        end: { x: end.x, y: end.y },
      },
    ];
  };

  const copyElement = useCallback(
    <T extends Area | LogicalSpace | Obstacle>(
      element: Element,
      transformation: PosePartial | undefined,
      params:
        | {
            type: 'Area';
          }
        | {
            type: 'LogicalSpace';
          }
        | {
            type: 'Obstacle';
          },
    ): T | undefined => {
      const { type } = params;

      const { getAreaById, getLogicalSpaceById, getObstacleById } =
        useFloorplanDesignStore.getState();

      canvasSvc.removeCanvasElements({
        method: 'byId',
        ids: [element.id],
      });

      const originalId = element.tags?.find((tag) => isUuid(tag));
      if (!originalId) return;

      let correspondingItem: Area | LogicalSpace | Obstacle | undefined;
      switch (type) {
        case 'LogicalSpace':
          correspondingItem = getLogicalSpaceById(originalId);
          break;
        case 'Obstacle':
          correspondingItem = getObstacleById(originalId);
          break;
        case 'Area':
          correspondingItem = getAreaById(originalId);
          break;
        default:
          assertNever(type);
      }

      if (!correspondingItem) return;

      const transformOffset = transformation?.position;
      if (!transformOffset) return;

      const offset = new Vector2(
        NumUtils.mmToMetersRoundedToMm(transformOffset.x),
        NumUtils.mmToMetersRoundedToMm(transformOffset.y),
      );
      const newItem = CopyPasteServiceFactory.getInstance()
        .createService()
        .copyItem<T>({ item: correspondingItem, type, offset } as Parameters<
          ReturnType<
            ReturnType<typeof CopyPasteServiceFactory.getInstance>['createService']
          >['copyItem']
        >[0]);
      if (!newItem) return;

      return newItem as T;
    },
    [canvasSvc],
  );

  const updateObstacle = useCallback(
    (
      obstacleElement: RectCenteredElement | LinePathElement,
      transformation: PosePartial | undefined,
      obstacleMap: Map<string, Obstacle>,
      obstacleUpdates: Obstacle[],
      newObstacles: Obstacle[],
    ): void => {
      const correspondingObstacle = obstacleMap.get(obstacleElement.id);

      if (!correspondingObstacle) {
        const copy = copyElement<Obstacle>(obstacleElement, transformation, {
          type: 'Obstacle',
        });
        if (!copy) return;
        newObstacles.push(copy);
        return;
      }

      if (obstacleElement.type === 'LinePath') {
        if (!isStableLinePathElement(obstacleElement)) return;

        // Same as angled highways: bake the final pose into absolute points
        const { position, rotation, points } = obstacleElement;
        const segments = updateSegmentsFromPose(
          correspondingObstacle.segments,
          position,
          rotation,
          points,
        );
        if (!segments) return;

        const updatedObstacle = {
          ...correspondingObstacle,
          segments,
        };

        obstacleUpdates.push(updatedObstacle);
        return;
      }

      if (!isStableRectCenteredElement(obstacleElement)) return;

      const segment = updateSegmentPoints(
        correspondingObstacle.segments[0],
        obstacleElement.position,
        obstacleElement.rotation,
        new Vector2(obstacleElement.scale.x, Math.abs(obstacleElement.scale.y)),
      );
      if (!segment) return;

      const updatedObstacle = {
        ...correspondingObstacle,
        segments: segment,
      };

      obstacleUpdates.push(updatedObstacle);
    },
    [copyElement],
  );

  const updateLogicalSpace = useCallback(
    (
      logicalSpaceElement: RectCenteredElement,
      transformation: PosePartial | undefined,
      logicalSpacesMap: Map<string, LogicalSpace>,
      logicalSpaceUpdates: LogicalSpace[],
      newLogicalSpaces: LogicalSpace[],
    ) => {
      const correspondingLogicalSpace = logicalSpacesMap.get(logicalSpaceElement.id);

      if (!correspondingLogicalSpace) {
        const copy = copyElement<LogicalSpace>(logicalSpaceElement, transformation, {
          type: 'LogicalSpace',
        });
        if (!copy) return;
        newLogicalSpaces.push(copy);
        return;
      }

      if (!isStableRectCenteredElement(logicalSpaceElement)) return;

      const { position, scale, rotation } = logicalSpaceElement;
      const rectangle = RectangleUtils.canvasRectCenterToFDDMRect(
        position.x / 1000,
        position.y / 1000,
        scale.x / 1000,
        scale.y / 1000,
        rotation.z,
      );
      const points = RectangleUtils.rectangleToPolygon(rectangle).map((corner) => {
        const point: Point = { x: corner.x, y: corner.y };
        return point;
      });
      if (!isAtLeast3(points)) return;
      const polygon: Polygon = {
        points,
      };

      const updatedLogicalSpace = {
        ...correspondingLogicalSpace,
        polygon,
      };

      logicalSpaceUpdates.push(updatedLogicalSpace);
    },
    [copyElement],
  );

  const calculateRectangle = (position: Vector3, rotation: Vector3, size: Vector2): Rectangle => ({
    centerX: NumUtils.mmToMeters(position.x),
    centerY: NumUtils.mmToMeters(position.y),
    width: NumUtils.mmToMeters(size.y),
    length: NumUtils.mmToMeters(size.x),
    angle: MathUtils.degToRad(rotation.z),
  });

  const updateArea = useCallback(
    (
      areaElement: RectCenteredElement | AngledHighwayElement | HighwayElement,
      transformation: PosePartial | undefined,
      areasMap: Map<string, Area>,
      areaUpdates: Area[],
      newAreas: Area[],
    ) => {
      const correspondingArea = areasMap.get(areaElement.id);
      if (!correspondingArea) {
        const copy = copyElement<Area>(areaElement, transformation, {
          type: 'Area',
        });
        if (!copy) return;

        newAreas.push(copy);
        return;
      }

      if (isStableAngledHighwayElement(areaElement) && isHighwayArea(correspondingArea)) {
        const { position, rotation, points } = areaElement;
        const segments = updateSegmentsFromPose(
          correspondingArea.segments,
          position,
          rotation,
          points,
        );
        if (!segments) return;

        const updatedArea = {
          ...correspondingArea,
          segments,
        };

        areaUpdates.push(updatedArea);
      } else if (isStableRectCenteredElement(areaElement)) {
        const rectangle = calculateRectangle(
          areaElement.position,
          areaElement.rotation,
          new Vector2(Math.abs(areaElement.scale.x), Math.abs(areaElement.scale.y)),
        );
        if (!rectangle) return;

        const updatedArea = {
          ...correspondingArea,
          rectangle,
        };
        // TODO: FDDM: get this action out if this scope and nto the parent scope
        areaUpdates.push(updatedArea);
      } else if (isStableHighwayElement(areaElement) && isHighwayArea(correspondingArea)) {
        const segment = updateSegmentPoints(
          correspondingArea.segments[0],
          areaElement.position,
          areaElement.rotation,
          new Vector2(areaElement.scale.x, Math.abs(areaElement.scale.y)),
        );

        if (!segment) return;

        const updatedArea = {
          ...correspondingArea,
          segments: segment,
        };

        areaUpdates.push(updatedArea);
      } else {
        logger.error(
          `Unhandled: expected area with areaType: ${correspondingArea.areaType} to be represented as a LinePath in the canvas. Got: ${areaElement.type}`,
        );
      }
    },
    [copyElement],
  );

  /** @description The template with the transformation applied to its params, to regenerate it */
  const getTransformedTemplate = useCallback(
    (template: Template, transformation: PosePartial | undefined): Template | undefined => {
      const angleOffset = transformation?.rotation?.z ?? 0;
      const positionOffset = transformation?.position ?? new Vector3();

      const canvas = useCanvasStore.getState().instance;
      if (!canvas) return;

      if (isLassie(template)) {
        return {
          ...template,
          params: {
            ...template.params,
            centerX: template.params.centerX + NumUtils.mmToMeters(positionOffset.x),
            centerY: template.params.centerY + NumUtils.mmToMeters(positionOffset.y),
            angle: template.params.angle + degToRad(angleOffset),
          },
        };
      }

      if (isHikStorage(template)) {
        return {
          ...template,
          params: {
            ...template.params,
            centerX: template.params.centerX + NumUtils.mmToMeters(positionOffset.x),
            centerY: template.params.centerY + NumUtils.mmToMeters(positionOffset.y),
            angle: template.params.angle + degToRad(angleOffset),
          },
        };
      }

      if (isLassieMini(template)) {
        return {
          ...template,
          params: {
            ...template.params,
            centerX: template.params.centerX + NumUtils.mmToMeters(positionOffset.x),
            centerY: template.params.centerY + NumUtils.mmToMeters(positionOffset.y),
            angle: template.params.angle + degToRad(angleOffset),
          },
        };
      }

      if (isShuttleRack(template)) {
        return {
          ...template,
          params: {
            ...template.params,
            centerX: template.params.centerX + NumUtils.mmToMeters(positionOffset.x),
            centerY: template.params.centerY + NumUtils.mmToMeters(positionOffset.y),
            angle: template.params.angle + degToRad(angleOffset),
          },
        };
      }

      logger.error('Unhandled template type');
    },
    [],
  );

  /** @description Regenerates all templates at once (one generateFloorplan); old → new group id */
  const updateTemplates = useCallback(
    (
      templates: Template[],
      transformation: PosePartial | undefined,
    ): Promise<Map<string, string>> =>
      tpsSvc.regenerateTemplates(
        templates.flatMap((template) => getTransformedTemplate(template, transformation) ?? []),
      ),
    [getTransformedTemplate, tpsSvc],
  );

  const updateGroup = useCallback(
    (
      group: Group,
      transformation: PosePartial | undefined,
      areasMap: Map<string, Area>,
      areaUpdates: Area[],
      newAreas: Area[],
      obstaclesMap: Map<string, Obstacle>,
      obstacleUpdates: Obstacle[],
      newObstacles: Obstacle[],
      logicalSpacesMap: Map<string, LogicalSpace>,
      logicalSpaceUpdates: LogicalSpace[],
      newLogicalSpaces: LogicalSpace[],
    ) => {
      const canvas = useCanvasStore.getState().instance;
      if (!canvas) return;

      const elementsInGroup = canvas.getElements(group.areaIds);

      elementsInGroup.forEach((item) => {
        if (isObstacleCanvasElement(item)) {
          updateObstacle(item, transformation, obstaclesMap, obstacleUpdates, newObstacles);
          return;
        }
        if (isLogicalSpaceCanvasElement(item)) {
          updateLogicalSpace(
            item,
            transformation,
            logicalSpacesMap,
            logicalSpaceUpdates,
            newLogicalSpaces,
          );
          return;
        }

        if (isAreaCanvasElement(item)) {
          updateArea(item, transformation, areasMap, areaUpdates, newAreas);
        }
      });
    },
    [updateArea, updateLogicalSpace, updateObstacle],
  );

  const transformEnded = useCallback(
    async (e: ElementsTransformEventArgs): Promise<void> => {
      const floorplanDesignState = useFloorplanDesignStore.getState();
      const { design } = floorplanDesignState;
      const {
        selectedTemplates,
        selectedGroups,
        selectedContainerIds,
        selectedContainerChildIds,
        topLevelSelectionIds,
      } = getTransformSelection(e.elements, floorplanDesignState);
      let selectionIdsToRestore = topLevelSelectionIds;

      try {
        await historySvc.batchAsync(async () => {
          const { areas, logicalSpaces, obstacles } = design;
          const areaUpdates: Area[] = [];
          const newAreas: Area[] = [];
          const areasMap = areasToFlatMap(areas);
          const logicalSpaceUpdates: LogicalSpace[] = [];
          const newLogicalSpaces: LogicalSpace[] = [];
          const logicalSpacesMap = new Map(logicalSpaces.map((ls) => [ls.id, ls]));
          const obstacleUpdates: Obstacle[] = [];
          const newObstacles: Obstacle[] = [];
          const obstaclesMap = new Map(obstacles.map((o) => [o.id, o]));

          const areaElements = e.elements
            .filter(isAreaCanvasElement)
            .filter((element) => !selectedContainerChildIds.has(element.id));
          const obstacleElements = e.elements
            .filter(isObstacleCanvasElement)
            .filter((element) => !selectedContainerChildIds.has(element.id));
          const logicalSpaceElements = e.elements
            .filter(isLogicalSpaceCanvasElement)
            .filter((element) => !selectedContainerChildIds.has(element.id));

          // update group
          selectedGroups.forEach((group) => {
            updateGroup(
              group,
              e.transformation,
              areasMap,
              areaUpdates,
              newAreas,
              obstaclesMap,
              obstacleUpdates,
              newObstacles,
              logicalSpacesMap,
              logicalSpaceUpdates,
              newLogicalSpaces,
            );
          });

          // update logical spaces
          logicalSpaceElements.forEach((item) => {
            updateLogicalSpace(
              item,
              e.transformation,
              logicalSpacesMap,
              logicalSpaceUpdates,
              newLogicalSpaces,
            );
          });

          // update obstacles
          obstacleElements.forEach((item) => {
            updateObstacle(item, e.transformation, obstaclesMap, obstacleUpdates, newObstacles);
          });

          // update areas
          areaElements.forEach((item) => {
            updateArea(item, e.transformation, areasMap, areaUpdates, newAreas);
          });

          /*
           * One fddm update for all changed items instead of one per item type, so this transform (and
           * its undo/redo) sends one generateFloorplan request. Separate requests cancel each other.
           */
          if (
            areaUpdates.length > 0 ||
            obstacleUpdates.length > 0 ||
            logicalSpaceUpdates.length > 0
          ) {
            const areaUpdateCollection = createEmptyAreas();
            areaUpdates.forEach((area) => pushAreaToItsCollection(areaUpdateCollection, area));
            fddmSvc.updateItems({
              areas: areaUpdateCollection,
              obstacles: obstacleUpdates,
              logicalSpaces: logicalSpaceUpdates,
            });
          }

          if (newLogicalSpaces.length > 0) fddmSvc.addLogicalSpaces(newLogicalSpaces);
          if (newObstacles.length > 0) fddmSvc.addObstacles(newObstacles);
          if (newAreas.length > 0) {
            const areaCollection = createEmptyAreas();
            newAreas.forEach((area) => {
              pushAreaToItsCollection(areaCollection, area);
            });
            fddmSvc.addAreas(areaCollection);
          }

          // update selection
          if (newAreas.length > 0 || newObstacles.length > 0 || newLogicalSpaces.length > 0) {
            const newSelectedIds = [
              ...newAreas.map((a) => a.id),
              ...newObstacles.map((o) => o.id),
              ...newLogicalSpaces.map((ls) => ls.id),
            ];

            if (selectedContainerIds.size > 0) {
              const retainedContainerIds = topLevelSelectionIds.filter((id) =>
                selectedContainerIds.has(id),
              );
              selectionIdsToRestore = [...retainedContainerIds, ...newSelectedIds];
            }

            appSvc.setSelectedIds(newSelectedIds);
          }

          const regeneratedTemplateIds = await updateTemplates(selectedTemplates, e.transformation);
          // Regenerated templates can get a new id: keep every one of them selected
          selectionIdsToRestore = selectionIdsToRestore.map(
            (id) => regeneratedTemplateIds.get(id) ?? id,
          );

          // MoveOrigin feature
          const moveOriginElement = e.elements.find((el) => el.id === ORIGIN_GROUP_ID);

          if (!moveOriginElement || moveOriginElement.type === 'GroupLogical') return;
          const positionInMm = moveOriginElement.position;

          if (!positionInMm) {
            logger.error('Expected canvas element to have a defined position.');
          } else {
            const originDeltaInverse = new Vector2(positionInMm.x, positionInMm.y).multiplyScalar(
              -1,
            );

            OriginServiceFactory.getInstance()
              .createService()
              .translateDesign({
                x: originDeltaInverse.x / METERS_TO_MILLIMETERS_FACTOR,
                y: originDeltaInverse.y / METERS_TO_MILLIMETERS_FACTOR,
              });
          }
        });
      } catch (error: unknown) {
        logger.error('Failed to update transformed selection.', error);
        restoreSelection(selectionIdsToRestore);
        return;
      }

      if (selectedTemplates.length > 0 || selectionIdsToRestore.length !== e.elements.length) {
        restoreSelection(selectionIdsToRestore);
      }
    },
    [
      historySvc,
      updateGroup,
      updateArea,
      fddmSvc,
      appSvc,
      updateLogicalSpace,
      updateObstacle,
      restoreSelection,
      updateTemplates,
    ],
  );

  return { transformEnded };
};
